<template>
  <v-dialog v-model="dialog" max-width="1200px" height="auto">
    <v-card>
      <!-----------------------------------------TITLE BAR----------------------------------------->
      <v-card-title class="redColor d-flex text-white">
        <!--TITLE-->
        <span class="headline">
          {{ $t("parts.inventory.search_list") }}
        </span>

        <v-spacer />

        <!--CLOSE BUTTON-->
        <v-btn variant="outlined" icon color="white" @click="close">
          <v-icon> mdi-close </v-icon>
        </v-btn>
      </v-card-title>
      <!------------------------------------------------------------------------------------------->
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="search"
              :label="$t('parts.inventory.search')"
              :loading="loadingGroups"
              variant="solo"
              color="grey-darken-4"
              append-inner-icon="mdi-magnify"
              single-line
              hide-details
              @keyup.enter="searchMethod"
              @click:append-inner="searchMethod"
            />
          </v-col>

          <!-- GROUPS LIST -->
          <v-col cols="12" md="6">
            <v-row v-if="loadingGroups" align="center" justify="center">
              <v-progress-circular
                class="ma-4"
                :size="50"
                color="red-darken-2"
                indeterminate
              />
            </v-row>

            <v-list v-else subheader>
              <v-subheader inset>Groups</v-subheader>

              <v-list-item
                v-for="group in partGroups"
                :key="group._id"
                :class="{
                  'selected-group':
                    selectedGroup && selectedGroup._id === group._id,
                }"
                @click="getTitles(group)"
              >
                <v-list-item-title
                  class="d-flex justify-space-between align-center"
                >
                  <a class="underline mt-2">
                    <span
                      v-if="group.title[$store.getters.language]"
                      v-html="
                        highlight(group.title[$store.getters.language], search)
                      "
                    />
                    <span
                      v-else-if="group.title.it"
                      v-html="highlight(group.title.it, search)"
                    />
                  </a>
                  <v-btn
                    icon="mdi-open-in-new"
                    variant="text"
                    @click.stop="searchGroup(group)"
                  />
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-col>

          <!-- TITLES LIST -->
          <v-col cols="12" md="6">
            <v-row v-if="loadingTitles" align="center" justify="center">
              <v-progress-circular
                class="ma-4"
                :size="50"
                color="red-darken-2"
                indeterminate
              />
            </v-row>

            <v-list v-else two-line subheader>
              <v-subheader inset>Titles</v-subheader>

              <v-list-item
                v-for="item in titles"
                :key="item._id"
                @click="searchTitle(item)"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <a class="underline">
                      <span
                        v-if="item.title[$store.getters.language]"
                        v-html="
                          highlight(item.title[$store.getters.language], search)
                        "
                      />
                      <span
                        v-else-if="item.title.it"
                        v-html="highlight(item.title.it, search)"
                      />
                    </a>
                  </v-list-item-title>

                  <v-list-item-subtitle>
                    <span
                      v-for="(element, index) in item.related"
                      :key="index"
                      v-html="
                        highlight(
                          element.title[$store.getters.language] ||
                            element.title.it,
                          search,
                        )
                      "
                    />
                  </v-list-item-subtitle>
                </v-list-item-content>

                <template #append>
                  <span>{{ item.parts?.count || 0 }}</span>
                </template>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import getRequest from "../../api/getRequest";

export default {
  components: {},
  data: () => ({
    dialog: false,
    search: "",
    loadingGroups: false,
    loadingTitles: false,
    partGroups: [],
    titles: [],
    selectedGroup: null,
  }),
  watch: {
    dialog(value) {
      if (!value) this.close();
    },
    search(value) {
      if (value) this.searchMethod();
      else {
        this.partGroups = [];
      }
    },
  },
  methods: {
    open() {
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.search = "";
      this.loadingGroups = false;
      this.loadingTitles = false;
      this.partGroups = [];
      this.titles = [];
      this.selectedGroup = null;
      this.$emit("close");
    },
    searchMethod() {
      this.loadingGroups = true;
      this.titles = [];
      getRequest(
        "/info-car/populatedpartgroups",
        `title=${this.search}&language=${this.$store.getters.language}`,
      )
        .then((response) => {
          this.partGroups = response.partgroups;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingGroups = false;
        });
    },
    searchGroup(group) {
      this.$emit("group", { group });
      this.close();
    },
    getTitles(group) {
      this.selectedGroup = group;
      this.loadingTitles = true;
      getRequest(
        "/info-car/populatedpartgroup",
        `id=${group._id}&role=${this.$store.getters.role}&institution_id=${this.$store.getters.institutionId}`,
      )
        .then((response) => {
          this.titles = response.parts;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingTitles = false;
        });
    },
    searchTitle(title) {
      this.$emit("title", { title });
      this.close();
    },
    highlight(title, search) {
      if (!title) return ""; // Handle undefined/null
      const text = typeof title === "string" ? title : String(title); // Ensure it's a string

      if (!search) {
        return `<span>${text}</span>`;
      }

      const lowerText = text.toLocaleLowerCase();
      const lowerSearch = search.toLocaleLowerCase();
      const index = lowerText.indexOf(lowerSearch);

      if (index === -1) {
        return `<span>${text}</span>`;
      }

      const result = [
        text.slice(0, index),
        "<mark>",
        text.slice(index, index + search.length),
        "</mark>",
        text.slice(index + search.length),
      ].join("");

      return `<span>${result}</span>`;
    },
  },
};
</script>
<style scoped>
.selected-group {
  background-color: #ebebeb;
}
</style>
